import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { base44 } from '@/api/base44Client';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import Button from '@/components/atelier/Button';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import SectionHeader from '@/components/atelier/SectionHeader';
import FAQAccordion from '@/components/atelier/FAQAccordion';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import NotFoundPage from '@/components/routing/NotFoundPage';
import QuickAnswer from '@/components/journal/QuickAnswer';
import { updateMetaTags, getCanonicalUrl } from '@/lib/seo';
import { getArticleMeta } from '@/lib/articleMeta';
import { BUSINESS } from '@/lib/businessConfig';
import { cmsPath, setHreflangPair, twinPath } from '@/lib/cmsLang';
import LangSwitch from '@/components/routing/LangSwitch';
import NewsletterSignup from '@/components/journal/NewsletterSignup';

const BOOKING_URL = BUSINESS.bookingUrl;
const ENGLISH_ARTICLES = new Set(['ai-hair-analysis-changing-consultations', 'what-is-face-metrics-pro-greece']);

function buildSchemas(article, lang = 'el') {
  const enRecord = lang === 'en';
  const url = `${BUSINESS.siteUrl}${cmsPath(article.slug, lang)}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: article.cover_image || BUSINESS.ogImage,
    datePublished: article.published_date,
    dateModified: article.updated_date || article.published_date,
    author: (!article.author || /Οικονομάκης|Ikonomakis/i.test(article.author))
      ? { '@type': 'Person', name: BUSINESS.founderName, url: `${BUSINESS.siteUrl}${BUSINESS.canonical.founder}`, sameAs: BUSINESS.founderSameAs }
      : { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      '@id': `${BUSINESS.siteUrl}/#organization`,
      name: BUSINESS.brandName,
      url: BUSINESS.siteUrl,
      logo: { '@type': 'ImageObject', url: BUSINESS.logoUrl },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: enRecord ? 'en' : (ENGLISH_ARTICLES.has(article.slug) ? 'en-GB' : 'el-GR'),
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1', '#quick-answer'] },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      enRecord
        ? { '@type': 'ListItem', position: 1, name: 'Home', item: `${BUSINESS.siteUrl}/en` }
        : { '@type': 'ListItem', position: 1, name: 'Αρχική', item: `${BUSINESS.siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Journal', item: `${BUSINESS.siteUrl}/journal` },
      { '@type': 'ListItem', position: 3, name: article.title, item: url },
    ],
  };
  const faqSchema = article.faq_items?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(enRecord ? { inLanguage: 'en' } : {}),
    mainEntity: article.faq_items.map((item) => ({ '@type': 'Question', name: item.question, acceptedAnswer: { '@type': 'Answer', text: item.answer } }))
  } : null;
  return [articleSchema, breadcrumbSchema, faqSchema].filter(Boolean);
}

// Page-level UI strings for EN CMS articles (/en/<slug>). Greek strings stay inline, unchanged.
const EN_UI = {
  home: 'Home', homeHref: '/en', related: ['Related services', 'Take the next', 'step.', 'Services and pages related to this article — with links to booking or more information.'],
  relatedNav: 'Related services', published: 'Published', updated: 'Updated', read: 'min read',
  authorBio: 'Hair expert, founder of Hair Community Greece and creator of Face Metrics Pro, with a premium approach to haircuts, colour and face-shape consultation.',
  authorName: 'Thodoris Ikonomakis', profile: 'View profile →',
  faq: ['FAQ', 'Frequently asked', 'questions.', 'The same questions are also available as structured data for AI assistants and search engines.'],
  cta: ['Want an answer', 'for your own hair?', 'Book a consultation at Ikonomakis Hair Atelier and get personal advice on your haircut, colour or hair transformation. English spoken.'],
  book: 'Book now →', notFound: 'The article you are looking for does not exist or has moved.', shortAnswer: 'Short answer',
};

export default function BlogArticlePage({ initialArticle = null, lang: langProp, alternate = null }) {
  const params = useParams();
  const lang = langProp || (initialArticle?.lang === 'en' ? 'en' : 'el');
  const enRecord = lang === 'en';
  const slug = initialArticle?.slug || params.slug || window.location.pathname.replace(/^\/(en\/)?/, '').split('?')[0];
  const [article, setArticle] = useState(initialArticle);
  const [loading, setLoading] = useState(!initialArticle);
  const [notFound, setNotFound] = useState(false);
  const staticMeta = getArticleMeta(slug);

  // First paint, before the record resolves: the route's own title/description from the
  // static registry, so the crawler never sees a component-level generic title.
  useLayoutEffect(() => {
    const meta = initialArticle
      ? { title: initialArticle.meta_title || initialArticle.title, description: initialArticle.meta_description || initialArticle.excerpt || '' }
      : staticMeta;
    if (!meta) return;
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
  }, [slug]);

  useEffect(() => {
    if (initialArticle && initialArticle.slug === slug) { setArticle(initialArticle); setLoading(false); return; }
    setLoading(true);
    // Retry once on failure; only a successful empty answer is a real 404 (noindex).
    const load = () => base44.functions.invoke('getPublicBlogArticle', { slug, lang });
    load()
      .catch(() => new Promise((r) => setTimeout(r, 800)).then(load))
      .then((res) => {
        const a = res.data?.article;
        if (a && (a.lang === 'en' ? 'en' : 'el') === lang) setArticle(a);
        else setNotFound(true);
        setLoading(false);
      })
      .catch(() => {}); // stay on the indexable loading view with the real H1
  }, [slug, lang]);

  useEffect(() => {
    if (!article) return;
    const pathname = cmsPath(article.slug, lang);
    updateMetaTags({
      title: article.meta_title || article.title,
      description: article.meta_description || article.excerpt || '',
      image: article.cover_image || BUSINESS.ogImage,
      url: getCanonicalUrl(pathname),
      type: 'article',
    });
    const english = enRecord || ENGLISH_ARTICLES.has(article.slug);
    document.documentElement.lang = english ? 'en' : 'el';
    document.querySelector('meta[property="og:locale"]')?.setAttribute('content', english ? 'en_GB' : 'el_GR');
    const twin = twinPath(article, lang, alternate);
    if (twin) {
      if (enRecord) setHreflangPair(twin, pathname);
      else if (twin.startsWith('/en/')) setHreflangPair(pathname, twin);
    }
  }, [article, lang, alternate]);

  // While the record loads, render the article's real H1 (registry) — never a blank shell.
  if (loading) return (
    <div className="bg-cream-bg min-h-screen">
      <TopBar />
      <Nav />
      <header className="px-[6vw] pt-[90px] pb-[70px] text-center">
        <div className="max-w-editorial mx-auto">
          <h1 className="font-serif text-display-xl text-warm-black font-medium tracking-[-3px] leading-[0.92] mb-7">
            {staticMeta?.h1 || staticMeta?.title || 'Journal'}
          </h1>
          {staticMeta?.description && (
            <p className="text-body-l text-muted leading-[1.65]">{staticMeta.description}</p>
          )}
        </div>
      </header>
      <Footer />
    </div>
  );

  if (notFound || !article) {
    return <NotFoundPage message={enRecord ? EN_UI.notFound : 'Το άρθρο που ψάχνεις δεν υπάρχει ή έχει μετακινηθεί.'} />;
  }

  const schemas = buildSchemas(article, lang);
  const twin = twinPath(article, lang, alternate);
  const switchHref = twin && (enRecord ? !twin.startsWith('/en/') : twin.startsWith('/en/')) ? twin : null;
  const dateLocale = enRecord ? 'en-GB' : 'el-GR';

  return (
    <div className="bg-cream-bg text-warm-black min-h-screen" lang={enRecord ? 'en' : undefined}>
      <TopBar />
      <Nav />
      {enRecord && (
        <div className="bg-warm-black text-cream text-tiny px-[6vw] py-2 flex justify-between flex-wrap gap-2">
          <span>English version{switchHref && <> · <a href={switchHref} hrefLang="el" lang="el" className="text-gold-light underline">Ελληνική έκδοση →</a></>}</span>
          <span>All staff speak English · Metro Line 3 → Chalandri</span>
        </div>
      )}
      {schemas.map((schema, i) => <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />)}

      <div className="px-[6vw] py-4 bg-cream-soft/40 border-b border-brown/10">
        <div className="max-w-container mx-auto text-tiny text-muted/80 flex gap-2 flex-wrap">
          <a href={enRecord ? EN_UI.homeHref : '/'} className="hover:text-warm-black">Home</a><span>/</span><a href="/journal" className="hover:text-warm-black">Journal</a><span>/</span><span className="text-warm-black">{article.title}</span>
          {!enRecord && <LangSwitch lang={lang} href={switchHref} />}
        </div>
      </div>

      <article>
        <header className="px-[6vw] pt-[90px] pb-[70px] text-center">
          <div className="max-w-editorial mx-auto">
            <EyebrowPill variant="dark" className="mb-7">{enRecord ? ({ "Χρώμα": "Colour", "Θεραπείες": "Treatments", "Κουρέματα": "Haircuts", "Φιλοσοφία": "Philosophy", "Styling": "Styling" }[article.category] || article.category) : article.category}</EyebrowPill>
            <h1 className="font-serif text-display-xl text-warm-black font-medium tracking-[-3px] leading-[0.92] mb-7">{article.title}</h1>
            <QuickAnswer text={article.excerpt || article.meta_description} label={enRecord || ENGLISH_ARTICLES.has(article.slug) ? 'Short answer' : 'Σύντομη απάντηση'} />
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-tiny uppercase tracking-wider text-muted">
              <span>
                <a href="/thodoris-ikonomakis-hair-expert" className="hover:text-gold transition-colors font-semibold">
                  {article.author || (enRecord ? EN_UI.authorName : 'Θοδωρής Οικονομάκης')}
                </a>
              </span>
              {article.published_date && (
                <time dateTime={article.published_date}>
                  {enRecord ? EN_UI.published : 'Δημοσιεύτηκε'}: {new Date(article.published_date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                </time>
              )}
              {article.updated_date && article.updated_date !== article.published_date && (
                <time dateTime={article.updated_date}>
                  {enRecord ? EN_UI.updated : 'Ενημερώθηκε'}: {new Date(article.updated_date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                </time>
              )}
              <span>{article.read_time_minutes || 7} {enRecord ? EN_UI.read : 'λεπτά ανάγνωση'}</span>
            </div>
          </div>
        </header>

        <section className="px-[6vw] pb-[80px]">
          <div className="max-w-editorial mx-auto bg-ivory rounded-card-xl p-8 md:p-12 shadow-card-soft">
            <div className="prose prose-stone max-w-none prose-headings:font-serif prose-headings:text-warm-black prose-p:text-muted prose-p:leading-[1.75] prose-a:text-gold prose-a:no-underline hover:prose-a:text-brown prose-li:text-muted prose-strong:text-warm-black">
              <ReactMarkdown components={{ h1: () => null, img: ({ node, ...p }) => <img {...p} alt={p.alt || article.title} loading="lazy" decoding="async" style={{ aspectRatio: '4 / 3', objectFit: 'cover', width: '100%' }} /> }}>
                {article.content}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      </article>

      {!enRecord && <NewsletterSignup />}

      {article.internal_links?.length > 0 && (
        <section aria-labelledby="related-services-heading" className="px-[6vw] py-[100px] bg-cream-soft">
          <div className="max-w-container mx-auto">
            {enRecord
              ? <SectionHeader id="related-services-heading" eyebrow={EN_UI.related[0]} title={EN_UI.related[1]} titleEm={EN_UI.related[2]} subtitle={EN_UI.related[3]} />
              : <SectionHeader id="related-services-heading" eyebrow="Σχετικές Υπηρεσίες" title="Κάνε το επόμενο" titleEm="βήμα." subtitle="Υπηρεσίες και σελίδες που σχετίζονται με αυτό το άρθρο — με links προς κράτηση ή περισσότερες πληροφορίες." />}
            <nav aria-label={enRecord ? EN_UI.relatedNav : 'Σχετικές υπηρεσίες'}>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 list-none m-0 p-0">
                {article.internal_links.map((link) => (
                  <li key={link.url}>
                    <a href={link.url} className="flex flex-col h-full bg-ivory rounded-card-l p-6 hover:shadow-card hover:-translate-y-1 transition-all duration-300 border border-brown/8 hover:border-gold/30">
                      <div className="text-gold text-[24px] mb-3">→</div>
                      <h3 className="font-serif text-[19px] text-warm-black mb-2 font-medium">{link.label}</h3>
                      <p className="text-tiny text-muted leading-[1.5] flex-1">{link.description}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </section>
      )}

      <section className="px-[6vw] py-[90px] bg-warm-black text-cream">
        <div className="max-w-editorial mx-auto flex flex-col md:flex-row gap-8 items-start md:items-center">
          <div className="w-20 h-20 rounded-full bg-gold text-warm-black flex items-center justify-center font-serif text-[34px]">Θ</div>
          <div>
            <div className="text-eyebrow uppercase tracking-wider text-gold-light mb-2">Author</div>
            <h2 className="font-serif text-[32px] mb-2">{enRecord ? EN_UI.authorName : 'Θοδωρής Οικονομάκης'}</h2>
            <p className="text-cream/75 leading-[1.7] mb-4">{enRecord ? EN_UI.authorBio : 'Hair expert, founder of Hair Community Greece και creator του Face Metrics Pro, με premium προσέγγιση σε κούρεμα, χρώμα και προσωπομετρική συμβουλευτική.'}</p>
            <Button variant="secondary" href="/thodoris-ikonomakis-hair-expert">{enRecord ? EN_UI.profile : 'Δες το προφίλ →'}</Button>
          </div>
        </div>
      </section>

      {article.faq_items?.length > 0 && (
        <section className="bg-cream-soft px-[6vw] py-[120px]">
          <div className="max-w-editorial mx-auto">
            {enRecord
              ? <SectionHeader eyebrow={EN_UI.faq[0]} title={EN_UI.faq[1]} titleEm={EN_UI.faq[2]} subtitle={EN_UI.faq[3]} center />
              : <SectionHeader eyebrow="FAQ" title="Συχνές" titleEm="ερωτήσεις." subtitle="Οι ίδιες ερωτήσεις είναι διαθέσιμες και ως structured data για AI και search engines." center />}
            <FAQAccordion items={article.faq_items} />
          </div>
        </section>
      )}

      <section className="px-[6vw] py-[120px]">
        {enRecord
          ? <FinalCTACard title={EN_UI.cta[0]} titleEm={EN_UI.cta[1]} description={EN_UI.cta[2]} ctaText={EN_UI.book} ctaHref={BOOKING_URL} ctaTarget="_blank" ctaRel="noopener noreferrer" />
          : <FinalCTACard title="Θέλεις απάντηση" titleEm="για τα δικά σου μαλλιά;" description="Κλείσε consultation στο Ikonomakis Hair Atelier και πάρε εξατομικευμένη καθοδήγηση για κούρεμα, χρώμα ή hair transformation." ctaText="Κράτηση Online →" ctaHref={BOOKING_URL} ctaTarget="_blank" ctaRel="noopener noreferrer" />}
      </section>

      <Footer />
    </div>
  );
}